const PREFIX = 'phill-dispute-answer-seen:';

/**
 * Whether this browser has already shown the notice that a remark was answered.
 *
 * Client-side only, and deliberately not an API: what the client does about the answer — approve, or
 * remark again — is already recorded, and "has read the notice" is not a fact anybody acts on. The
 * durable record is the answered remark on the report, which stays until they reply to it.
 *
 * So this is only about not reopening the same dialog on every navigation. Losing the flag is harmless
 * in a way losing the note would not be, which is why `clearSession` is left alone: being told twice
 * costs a click, and the report says the same thing with the dialog closed.
 *
 * Keyed by the answer, not by the report: a remark can be raised, answered, raised again and answered
 * again on one report, and each of those answers is news. Keyed by report alone, closing the first
 * notice silenced every later one on that report for good.
 *
 * Keyed by report rather than by person, though. A client's browser is their own, and a colleague who
 * sees a notice meant for someone else reads who raised the remark on the box behind it.
 */
const keyFor = (sprintProductId: string, answeredAt?: string): string =>
  answeredAt ? `${PREFIX}${sprintProductId}:${answeredAt}` : `${PREFIX}${sprintProductId}`;

/** The answer's own instant, normalised: it arrives as a string over the wire and a Date in the tests. */
const stampOf = (answeredAt?: Date | string | null): string | undefined => {
  if (!answeredAt) return undefined;
  const answered = new Date(answeredAt);
  return Number.isNaN(answered.getTime()) ? undefined : answered.toISOString();
};

export const hasSeenDisputeAnswer = (sprintProductId?: string, answeredAt?: Date | string | null): boolean => {
  if (!sprintProductId) return true;
  try {
    return localStorage.getItem(keyFor(sprintProductId, stampOf(answeredAt))) === '1';
  } catch {
    // Storage unavailable (private mode, blocked cookies). Treated as seen: a dialog that cannot
    // remember being closed would reopen on every render, which is worse than never opening.
    return true;
  }
};

export const markDisputeAnswerSeen = (sprintProductId?: string, answeredAt?: Date | string | null): void => {
  if (!sprintProductId) return;
  try {
    // The report's older answers are dropped as this one is written, so a report keeps one flag however
    // many times a remark is raised and answered on it.
    const own = `${PREFIX}${sprintProductId}`;
    Object.keys(localStorage)
      .filter(key => key === own || key.startsWith(`${own}:`))
      .forEach(key => localStorage.removeItem(key));

    localStorage.setItem(keyFor(sprintProductId, stampOf(answeredAt)), '1');
  } catch {
    // Nothing to do: the notice shows again next time, and the report still carries the answer.
  }
};
